import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories } from "@/mocks/categories";

const features = [
  {
    icon: "ri-database-2-line",
    title: "丰富的内容聚合",
    desc: "覆盖动作、爱情、科幻、悬疑等多种类型，持续整理与更新影片信息。",
  },
  {
    icon: "ri-filter-3-line",
    title: "细致的分类筛选",
    desc: "支持按类型、地区、年代与排序组合筛选，帮助你更快定位心仪影片。",
  },
  {
    icon: "ri-newspaper-line",
    title: "及时的影视资讯",
    desc: "汇聚影视动态与行业观察，让你在观影之外也能了解圈内大小事。",
  },
  {
    icon: "ri-smartphone-line",
    title: "贴心的多端体验",
    desc: "PC 与移动端均经过适配，随时随地浏览片单与资讯。",
  },
];

export default function About() {
  usePageMeta({
    title: "关于我们 - 电影天堂",
    description:
      "了解电影天堂的品牌定位、联系方式与使用须知。电影天堂专注于电影资源信息与影视资讯发布，为电影爱好者提供便捷的内容浏览体验。",
    keywords: "关于电影天堂,电影天堂介绍,联系方式,使用须知,版权声明",
  });

  return (
    <div className="mx-auto w-full max-w-[1080px] px-4 py-8 md:px-6 md:py-12">
      <nav className="mb-5 flex flex-wrap items-center gap-1.5 text-sm text-foreground-600">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900">关于我们</span>
      </nav>

      <header className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-10">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
          <i className="ri-film-fill"></i>
          关于电影天堂
        </span>
        <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
          做电影爱好者身边的观影指南
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-8 text-foreground-700 md:text-base">
          电影天堂聚焦电影资源信息与影视资讯发布，以内容丰富、分类细致、资讯及时为核心诉求。
          我们希望帮助每一位电影爱好者快速查找最新影片信息、按需筛选片单，并了解影视行业的最新动态，
          让“找电影、看资讯”这件事变得简单而愉快。
        </p>
      </header>

      <section className="mt-10">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">我们提供什么</h2>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {features.map((f) => (
            <div
              key={f.title}
              className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                <i className={`${f.icon} text-xl`}></i>
              </span>
              <div>
                <h3 className="font-heading text-base font-bold text-foreground-950">{f.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">热门分类直达</h2>
        <div className="mt-5 flex flex-wrap gap-2">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
            >
              <i className={`${c.icon}`}></i>
              {c.name}
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <i className="ri-mail-line text-primary-600"></i>
            联系方式
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            如果你对内容有任何建议或反馈，欢迎通过以下邮箱联系我们：
          </p>
          <div className="mt-4 flex items-center gap-2 rounded-md bg-secondary-50 px-4 py-3 text-sm text-foreground-800">
            <i className="ri-at-line text-primary-600"></i>
            <span className="font-medium">contact@movie-paradise.demo</span>
          </div>
          <p className="mt-3 text-xs text-foreground-500">
            * 该邮箱为演示示意地址，当前站点未接入真实邮件服务。
          </p>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-6">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <i className="ri-shield-check-line text-primary-600"></i>
            使用须知
          </h2>
          <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-foreground-600">
            <li className="flex gap-2">
              <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-accent-500"></i>
              本站所有影片与资讯内容均为演示用虚构样本，不代表真实作品或报道。
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-accent-500"></i>
              站点不提供任何真实资源下载，也不涉及任何真实个人隐私信息。
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-accent-500"></i>
              收藏等个性化功能仅保存在当前浏览器本地，不会上传至服务器。
            </li>
          </ul>
        </div>
      </section>

      <section className="mt-10 rounded-lg border border-background-200 bg-primary-50 p-6 md:p-8">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
          <i className="ri-copyright-line text-primary-700"></i>
          版权声明
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground-700">
          © 2025 电影天堂 Movie Paradise。本站为产品演示项目，站内所有文字、图片与数据均为演示而虚构生成，
          不构成对任何真实影片、机构或个人的描述。请勿将本站内容用于任何商业用途或作为真实信息来源。
        </p>
      </section>

      <div className="mt-10 flex flex-wrap gap-3">
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
        >
          <i className="ri-home-4-line"></i>
          返回首页
        </Link>
        <Link
          to="/news"
          className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
        >
          <i className="ri-newspaper-line"></i>
          浏览资讯
        </Link>
      </div>
    </div>
  );
}